import { Chip, Grid, Skeleton, SxProps, Theme, Typography } from '@mui/material'
import React from 'react'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { getPerformanceStatsColumnTranslationKey } from '../../../utils/hooks/performanceStats/useStatsColumns'
import { useIsMobileLayout } from '../../../utils/hooks/useIsMobileLayout'
import { StyledFormLabel } from '../../form/styled'
import { DefaultTimeseriesStatStyle, TimeseriesStatStyle, valueFormatter } from '../statsConf'
import { isNil } from 'lodash-es'

const statsWidgetSx =
  (color: string, isDisplayed: boolean, isVertical: boolean): SxProps<Theme> =>
  (theme) => ({
    borderBottom: 5,
    borderBottomColor: color,
    borderRadius: 0,
    borderRight: isVertical ? 0 : 1,
    borderRightColor: 'divider',
    py: isVertical ? 3 : 5,
    ...(isVertical && {
      boxSizing: 'border-box',
      height: '65px',
      minHeight: '65px',
    }),
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[100],
    textAlign: 'center',
    width: '100%',
    opacity: isDisplayed ? undefined : 0.3,
  })

export const StatsWidget: React.FC<{
  name: string
  value: string
  color: string
  onClick?: () => void
  isDisplayed: boolean
  loading: boolean
  isVertical?: boolean
}> = ({ name, value, color, onClick, isDisplayed, loading, isVertical = false }): JSX.Element => {
  return (
    <Grid size={isVertical ? 12 : 'grow'}>
      <Chip
        sx={statsWidgetSx(color, isDisplayed, isVertical)}
        onClick={onClick}
        label={
          <>
            <StyledFormLabel sx={{ fontSize: 15, mb: 1 }}>{name}</StyledFormLabel>
            {loading ? (
              <Skeleton height={30} variant="text" width={100} />
            ) : (
              <Typography variant="h1">{value}</Typography>
            )}
          </>
        }
      />
    </Grid>
  )
}

interface TimeseriesWidgetsProps<T extends Record<string, number>> {
  total: T
  statStyles: Record<keyof T, TimeseriesStatStyle>
  chartItemsVisibility: Record<keyof T, boolean>
  handleToggleVisibility?: (name: keyof T) => void
  translationPrefix: string
  translateValues: boolean
  loading?: boolean
}

const timeseriesWidgets = <T extends Record<string, number>>({
  total: data,
  statStyles,
  chartItemsVisibility,
  handleToggleVisibility,
  translationPrefix,
  translateValues = true,
  loading = false,
}: TimeseriesWidgetsProps<T>): JSX.Element => {
  const t = useAppTranslations()
  const isMobile = useIsMobileLayout()

  return (
    <Grid
      container
      direction={isMobile ? 'column' : 'row'}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        mb: 3,
        my: 1,
        overflow: 'hidden',
      })}
    >
      {Object.entries(chartItemsVisibility).flatMap(([name, isVisible]) => {
        const value = data[name as keyof T] ?? 0
        const statStyle = statStyles[name as keyof T] ?? DefaultTimeseriesStatStyle

        if (isNil(statStyle)) return []

        return (
          <StatsWidget
            key={name.toString()}
            name={!translateValues ? statStyle.name : t(getPerformanceStatsColumnTranslationKey(statStyle.name))}
            value={valueFormatter(statStyle, value)}
            color={statStyle.color ?? 'FFF'}
            onClick={handleToggleVisibility ? () => handleToggleVisibility(name as keyof T) : undefined}
            isDisplayed={isVisible}
            loading={loading}
            isVertical={isMobile}
          />
        )
      })}
    </Grid>
  )
}

export default timeseriesWidgets
